// saga.ts
import { call, delay, put, takeLatest } from 'redux-saga/effects';
import { AxiosResponse } from 'axios';
import { ISagaFunc } from 'services/actionConfigs';
import { setPageLoading } from 'widgets/PageLoading';
import api from './apis';
import { actions } from './slice';
import {
  GetTicketListParams,
  GetTicketSummaryParams,
  NewTicket,
  Ticket,
  TicketDetail,
  TicketSummary,
  UpdateTicketRequest,
} from './types';
import { PaginatedList } from 'services/commonTypes';

const getTicketList: ISagaFunc<GetTicketListParams> = function* ({ payload }) {
  yield delay(200);
  setPageLoading(true);
  try {
    const res: AxiosResponse<{ data: PaginatedList<Ticket> }> = yield call(
      api.get.list,
      payload
    );
    yield put(actions.api.get.list.success(res.data.data));
  } catch (error: any) {
    yield put(
      actions.api.get.list.fail(
        error.response?.data?.message || 'Failed to fetch ticket list'
      )
    );
  } finally {
    setPageLoading(false);
  }
};

const getTicketSummary: ISagaFunc<GetTicketSummaryParams> = function* ({
  payload,
}) {
  yield delay(200);
  setPageLoading(true);
  try {
    const res: AxiosResponse<{ data: TicketSummary }> = yield call(
      api.get.summary,
      payload
    );
    yield put(actions.api.get.summary.success(res.data.data));
  } catch (error: any) {
    yield put(
      actions.api.get.summary.fail(
        error.response?.data?.message || 'Failed to fetch ticket summary'
      )
    );
  } finally {
    setPageLoading(false);
  }
};

const getNewTicket = function* () {
  yield delay(200);
  setPageLoading(true);
  try {
    const res: AxiosResponse<{ data: NewTicket }> = yield call(
      api.get.newTicket
    );
    yield put(actions.api.get.newTicket.success(res.data.data));
  } catch (error: any) {
    yield put(
      actions.api.get.newTicket.fail(
        error.response?.data?.message || 'Failed to create new ticket'
      )
    );
  } finally {
    setPageLoading(false);
  }
};

const getTicketDetail: ISagaFunc<string> = function* ({ payload: billId }) {
  yield delay(200);
  setPageLoading(true);
  try {
    const res: AxiosResponse<{ data: TicketDetail }> = yield call(
      api.get.detail,
      billId
    );
    yield put(actions.api.get.detail.success(res.data.data));
  } catch (error: any) {
    yield put(
      actions.api.get.detail.fail(
        error.response?.data?.message || 'Failed to fetch ticket detail'
      )
    );
  } finally {
    setPageLoading(false);
  }
};

const getOnHoldTickets = function* () {
  yield delay(200);
  setPageLoading(true);
  try {
    const res: AxiosResponse<{ data: Ticket[] }> = yield call(api.get.onHold);
    yield put(actions.api.get.onHold.success(res.data.data));
  } catch (error: any) {
    yield put(
      actions.api.get.onHold.fail(
        error.response?.data?.message || 'Failed to fetch on-hold tickets'
      )
    );
  } finally {
    setPageLoading(false);
  }
};

const updateTicket: ISagaFunc<UpdateTicketRequest> = function* ({ payload }) {
  yield delay(200);
  setPageLoading(true);
  try {
    const res: AxiosResponse<{ data: string }> = yield call(
      api.post.update,
      payload
    );
    yield put(actions.api.post.update.success(res.data.data));
    // Refresh ticket detail after update
    if (payload.billId) {
      yield put(actions.api.get.detail.fetch(payload.billId));
    }
  } catch (error: any) {
    yield put(
      actions.api.post.update.fail(
        error.response?.data?.message || 'Failed to update ticket'
      )
    );
  } finally {
    setPageLoading(false);
  }
};

export default function* sagas() {
  yield takeLatest(actions.api.get.list.fetch.type, getTicketList);
  yield takeLatest(actions.api.get.summary.fetch.type, getTicketSummary);
  yield takeLatest(actions.api.get.newTicket.fetch.type, getNewTicket);
  yield takeLatest(actions.api.get.detail.fetch.type, getTicketDetail);
  yield takeLatest(actions.api.get.onHold.fetch.type, getOnHoldTickets);
  yield takeLatest(actions.api.post.update.fetch.type, updateTicket);
}
